import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiCamera, mdiClose, mdiFileImage } from '@mdi/js'
import { Button } from '../ui/button'

/** A photo picked for the declaration, with the object URL its thumbnail uses. */
export type PickedPhoto = {
    file: File
    /** Stable key so React does not remount every thumbnail on each add. */
    id: string
}

type Props = {
    value: PickedPhoto[]
    onChange: (photos: PickedPhoto[]) => void
    disabled?: boolean
}

/** Enough to show a problem from two angles and a wide shot; past that a
 * declaration turns into an upload session. */
export const MAX_PHOTOS = 5
/** Phone cameras produce 3–8 MB shots; anything past this is not a photo. */
const MAX_BYTES = 15 * 1024 * 1024

/**
 * HEIC/HEIF has been the default capture format on iOS since iOS 11, and no
 * browser but Safari can decode it — feeding `URL.createObjectURL(file)`
 * straight into an `<img>` produces the browser's broken-image icon before
 * the resident even submits the declaration (spec 2026-09-15-03). There is no
 * server round-trip to negotiate through at this point in the flow (nothing
 * is persisted until submit, and that "nothing persisted" contract is
 * deliberately not changed here), so the fix is client-side: skip the native
 * preview for a file we know a browser can't decode and show a filename/icon
 * chip instead. Recent Safari reports `image/heic` / `image/heif` reliably,
 * but some pickers still hand back an empty or generic type for it, so the
 * file extension is the fallback signal.
 */
function isHeicOrHeif(file: File): boolean {
    const type = file.type.toLowerCase()
    if (type === 'image/heic' || type === 'image/heif') return true
    if (type && type !== 'application/octet-stream') return false
    return /\.hei[cf]$/i.test(file.name)
}

/**
 * Photographs taken with a declaration.
 *
 * These document an EVENT — the leak, the forced door, the damage — not the
 * equipment. That is why they ride on the ticket rather than replacing the
 * asset's reference picture: the ticket gives each one a date, an author and a
 * stated problem, which a photo filed on the thing itself would never carry.
 *
 * `capture="environment"` makes a phone open the rear camera straight away
 * rather than a file browser, which is the whole interaction on the surface
 * this was written for.
 */
export function DeclarationPhotos({ value, onChange, disabled = false }: Props) {
    const { t } = useTranslation('assets')
    const inputRef = useRef<HTMLInputElement>(null)
    const [error, setError] = useState<string | null>(null)
    const [previews, setPreviews] = useState<Record<string, string>>({})

    // Object URLs leak unless revoked, and this list changes on every add.
    // A HEIC/HEIF file gets no object URL at all — the browser can't decode
    // it, so the preview renders a file chip instead (see isHeicOrHeif).
    useEffect(() => {
        const urls: Record<string, string> = {}
        for (const photo of value) {
            if (isHeicOrHeif(photo.file)) continue
            urls[photo.id] = URL.createObjectURL(photo.file)
        }
        setPreviews(urls)
        return () => { for (const url of Object.values(urls)) URL.revokeObjectURL(url) }
    }, [value])

    const addFiles = (files: FileList | null) => {
        if (!files || files.length === 0) return
        setError(null)
        const accepted: PickedPhoto[] = []
        for (const file of Array.from(files)) {
            if (value.length + accepted.length >= MAX_PHOTOS) {
                setError(t('declare.photos_too_many', { count: MAX_PHOTOS }))
                break
            }
            // A HEIC/HEIF file can report an empty or generic MIME type (see
            // isHeicOrHeif above) — without the OR here, such a file fails
            // this gate and is silently dropped before it ever reaches the
            // chip-instead-of-<img> fallback below: worse than the broken
            // preview this component was built to avoid, since nothing at
            // all shows for the resident's pick.
            if (!file.type.startsWith('image/') && !isHeicOrHeif(file)) continue
            if (file.size > MAX_BYTES) {
                setError(t('declare.photos_too_large'))
                continue
            }
            accepted.push({ file, id: `${file.name}-${file.size}-${file.lastModified}-${value.length + accepted.length}` })
        }
        if (accepted.length) onChange([...value, ...accepted])
    }

    return (
        <div data-testid="declaration-photos">
            {value.length > 0 && (
                <ul className="mb-2 flex flex-wrap gap-2" data-testid="declaration-photos-list">
                    {value.map((photo) => (
                        <li key={photo.id} className="relative">
                            {isHeicOrHeif(photo.file) ? (
                                <div
                                    className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-md border border-border bg-muted p-1"
                                    title={photo.file.name}
                                    data-testid={`declaration-photo-chip-${photo.id}`}
                                >
                                    <AppIcon tone="muted" path={mdiFileImage} size="md" />
                                    <span className="w-full truncate text-center text-[10px] text-muted-foreground">
                                        {photo.file.name}
                                    </span>
                                </div>
                            ) : (
                                <img
                                    src={previews[photo.id]}
                                    alt={t('declare.photo_alt')}
                                    className="h-20 w-20 rounded-md border border-border object-cover"
                                    data-testid={`declaration-photo-preview-${photo.id}`}
                                />
                            )}
                            <button
                                type="button"
                                onClick={() => onChange(value.filter((p) => p.id !== photo.id))}
                                disabled={disabled}
                                aria-label={t('declare.photo_remove')}
                                title={t('declare.photo_remove')}
                                className="absolute -right-1.5 -top-1.5 rounded-full bg-destructive p-0.5 text-destructive-foreground shadow-sm"
                                data-testid={`declaration-photo-remove-${photo.id}`}
                            >
                                <AppIcon path={mdiClose} size="xs" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="hidden"
                data-testid="declaration-photos-input"
                onChange={(e) => {
                    addFiles(e.target.files)
                    // Reset so picking the same file twice still fires a change.
                    e.target.value = ''
                }}
            />
            <Button
                type="button"
                variant="outline"
                className="h-11 w-full"
                disabled={disabled || value.length >= MAX_PHOTOS}
                onClick={() => inputRef.current?.click()}
                data-testid="declaration-photos-add"
            >
                <AppIcon path={mdiCamera} size="md" className="mr-2" />
                {value.length === 0
                    ? t('declare.photos_add')
                    : t('declare.photos_add_more', { count: value.length, max: MAX_PHOTOS })}
            </Button>
            {error && <p className="mt-1 text-xs text-destructive" data-testid="declaration-photos-error">{error}</p>}
        </div>
    )
}
